import React, { useEffect, useRef, useState } from "react";
import {
  copyLink,
  doShare,
  facebookShareUrl,
  linkedInShareUrl,
  mailtoUrl,
  telegramShareUrl,
  twitterIntentUrl,
  whatsappShareUrl,
  type ShareContent,
} from "../lib/share";
import {
  IconFacebook,
  IconLink,
  IconLinkedIn,
  IconMail,
  IconShare,
  IconTwitterX,
  IconWhatsApp,
  IconX,
} from "./icons";

/**
 * A share button that tries the native share sheet first (iOS/Android/
 * modern mobile browsers) and falls back to an in-app popover with
 * X / Facebook / LinkedIn / WhatsApp / Telegram / copy / email.
 */
export function ShareButton({
  content,
  variant = "chip",
  label = "Share",
  "data-testid": testId,
}: {
  /** Sync or async — use async when minting a share token server-side. */
  content: () => ShareContent | Promise<ShareContent>;
  variant?: "chip" | "icon" | "primary";
  label?: string;
  "data-testid"?: string;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverContent, setPopoverContent] = useState<ShareContent | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const onClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const c = await content();
      const result = await doShare(c);
      if (result === "fallback-needed") {
        setPopoverContent(c);
        setPopoverOpen(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const baseClass =
    variant === "icon"
      ? "share-icon-btn"
      : variant === "primary"
      ? "share-primary-btn"
      : "chip";

  return (
    <>
      <button
        className={baseClass}
        onClick={onClick}
        aria-label={label}
        data-testid={testId ?? "share-button"}
        disabled={busy}
        style={busy ? { opacity: 0.55 } : undefined}
      >
        <IconShare />
        {variant !== "icon" && <span>{busy ? "…" : label}</span>}
      </button>
      {popoverOpen && popoverContent && (
        <SharePopover
          content={popoverContent}
          onClose={() => { setPopoverOpen(false); setCopied(false); }}
          copied={copied}
          onCopied={() => setCopied(true)}
        />
      )}
    </>
  );
}

function SharePopover({
  content,
  onClose,
  copied,
  onCopied,
}: {
  content: ShareContent;
  onClose: () => void;
  copied: boolean;
  onCopied: () => void;
}) {
  const firstBtn = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    firstBtn.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleCopy = async () => {
    const ok = await copyLink(content.url);
    if (ok) onCopied();
  };

  return (
    <div
      className="sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Share"
      data-testid="share-popover"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sheet share-sheet">
        <div className="sheet-handle" />
        <div className="share-head">
          <div>
            <span className="sub">SHARE</span>
            <h2>{content.title}</h2>
          </div>
          <button
            className="chip"
            onClick={onClose}
            aria-label="Close"
            data-testid="share-close"
          >
            <IconX />
          </button>
        </div>
        <div className="share-preview">
          {content.text}
          <div className="share-preview-url">{content.url}</div>
        </div>
        <div className="share-grid">
          <ShareTile
            href={twitterIntentUrl(content)}
            label="X"
            innerRef={firstBtn}
            className="share-tile-x"
            data-testid="share-twitter"
          >
            <IconTwitterX />
          </ShareTile>
          <ShareTile
            href={facebookShareUrl(content)}
            label="Facebook"
            className="share-tile-fb"
            data-testid="share-facebook"
          >
            <IconFacebook />
          </ShareTile>
          <ShareTile
            href={whatsappShareUrl(content)}
            label="WhatsApp"
            className="share-tile-wa"
            data-testid="share-whatsapp"
          >
            <IconWhatsApp />
          </ShareTile>
          <ShareTile
            href={telegramShareUrl(content)}
            label="Telegram"
            className="share-tile-tg"
            data-testid="share-telegram"
          >
            <IconLink />
          </ShareTile>
          <ShareTile
            href={linkedInShareUrl(content)}
            label="LinkedIn"
            className="share-tile-li"
            data-testid="share-linkedin"
          >
            <IconLinkedIn />
          </ShareTile>
          <ShareTile
            href={mailtoUrl(content)}
            label="Email"
            className="share-tile-mail"
            data-testid="share-email"
          >
            <IconMail />
          </ShareTile>
          <button
            className="share-tile share-tile-copy"
            onClick={handleCopy}
            data-testid="share-copy"
          >
            <IconLink />
            <span>{copied ? "Copied!" : "Copy link"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareTile({
  href,
  label,
  className = "",
  innerRef,
  children,
  ...rest
}: {
  href: string;
  label: string;
  className?: string;
  innerRef?: React.Ref<HTMLAnchorElement>;
  children: React.ReactNode;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`share-tile ${className}`}
      ref={innerRef}
      {...rest}
    >
      {children}
      <span>{label}</span>
    </a>
  );
}
